/**
 * AdminContactFormInbox — /admin/contact
 *
 * Triage view for messages coming from the SocialPulse public /contact
 * form. Admin-only. Filters by status, search by text, expand to see
 * full message, one-click status changes.
 *
 * Backed by GET /api/admin/contact + PATCH /api/admin/contact/{id}.
 * (The unrelated /admin/contact-requests view handles memorial takedown
 *  requests — different collection, different UI.)
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Inbox, Search, Loader2, RefreshCw, Mail, ExternalLink,
  CheckCircle2, Clock, AlertOctagon, ArrowLeft,
} from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const STATUSES = [
  { key: "all",         label: "All" },
  { key: "new",         label: "New" },
  { key: "in_progress", label: "In progress" },
  { key: "resolved",    label: "Resolved" },
  { key: "spam",        label: "Spam" },
];

const NEXT_STATUS = {
  new:         "in_progress",
  in_progress: "resolved",
  resolved:    "new",
  spam:        "new",
};

export default function AdminContactFormInbox() {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ all: 0, new: 0, in_progress: 0, resolved: 0, spam: 0 });
  const [status, setStatus] = useState("new");
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isAdmin = !!user && (user.is_admin === true || user.platform_role === "super_admin" || user.platform_role === "admin");

  useEffect(() => { document.title = "Contact Inbox · Admin · SocialPulse"; }, []);

  async function load() {
    if (!isAdmin) return;
    setLoading(true);
    setError(null);
    try {
      const params = { limit: 100 };
      if (status !== "all") params.status = status;
      if (q.trim()) params.q = q.trim();
      const r = await http.get("/admin/contact", { params });
      setItems(r.data.items || []);
      setCounts(r.data.counts || {});
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't load inbox.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { if (isAdmin) load(); /* eslint-disable-next-line */ }, [isAdmin, status]);

  async function updateStatus(id, newStatus) {
    try {
      const r = await http.patch(`/admin/contact/${id}`, { status: newStatus });
      setItems((xs) => xs.map((it) => (it.id === id ? r.data : it)));
      load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Update failed.");
    }
  }

  const selected = useMemo(() => items.find((i) => i.id === selectedId), [items, selectedId]);

  if (authLoading) {
    return <div className="min-h-screen grid place-items-center text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /></div>;
  }
  if (!isAdmin) {
    return (
      <div className="min-h-screen grid place-items-center bg-white px-6">
        <div className="max-w-md text-center">
          <AlertOctagon className="w-8 h-8 text-rose-500 mx-auto" />
          <h1 className="mt-4 font-serif font-bold text-slate-900 text-2xl">Admin only</h1>
          <p className="mt-2 text-slate-600 text-sm">You need admin access to view the contact inbox.</p>
          <Link to="/" className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-violet-700 hover:text-violet-900">
            <ArrowLeft className="w-4 h-4" /> Back home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="admin-contact-inbox">
      <header className="border-b border-slate-200 bg-white sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-10 h-16 flex items-center gap-3">
          <Link to="/" data-testid="admin-back-home" className="w-9 h-9 grid place-items-center rounded-full hover:bg-slate-100 text-slate-600">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <span className="w-9 h-9 grid place-items-center rounded-xl bg-gradient-to-br from-emerald-500 to-violet-600 text-white shadow-sm">
            <Inbox className="w-4 h-4" strokeWidth={2.5} />
          </span>
          <h1 className="font-serif font-bold text-slate-900 text-lg">Contact Inbox</h1>
          <button
            onClick={load}
            data-testid="admin-inbox-refresh"
            className="ml-auto w-9 h-9 grid place-items-center rounded-full hover:bg-slate-100 text-slate-600"
            title="Refresh"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </button>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-10 py-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <div className="flex items-center gap-1 overflow-x-auto -mx-1 px-1" data-testid="admin-inbox-tabs">
          {STATUSES.map((s) => (
            <button
              key={s.key}
              onClick={() => setStatus(s.key)}
              data-testid={`admin-inbox-tab-${s.key}`}
              className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                status === s.key ? "bg-violet-600 text-white" : "bg-slate-50 text-slate-600 hover:bg-slate-100"
              }`}
            >
              {s.label}
              <span className={`ml-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${status === s.key ? "bg-white/20 text-white" : "bg-slate-200 text-slate-700"}`}>
                {counts[s.key] ?? 0}
              </span>
            </button>
          ))}
        </div>
        <form className="relative sm:ml-auto sm:min-w-[280px]" onSubmit={(e) => { e.preventDefault(); load(); }}>
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, text…"
            data-testid="admin-inbox-search"
            className="w-full h-9 pl-9 pr-3 rounded-full ring-1 ring-slate-200 focus:ring-2 focus:ring-violet-500 focus:outline-none text-sm text-slate-800 placeholder:text-slate-400 bg-white"
          />
        </form>
      </div>

      <main className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-10 pb-16 grid lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1.5fr)] gap-6">
        <ul className="rounded-2xl ring-1 ring-slate-200 bg-white divide-y divide-slate-100 overflow-hidden max-h-[75vh] overflow-y-auto" data-testid="admin-inbox-list">
          {loading && items.length === 0 && (
            <li className="p-8 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </li>
          )}
          {!loading && items.length === 0 && !error && (
            <li className="p-10 text-center text-sm text-slate-500">
              Inbox zero. Nothing to triage.
            </li>
          )}
          {error && (
            <li className="p-6 text-sm text-rose-700 bg-rose-50">{error}</li>
          )}
          {items.map((it) => (
            <li key={it.id}>
              <button
                onClick={() => setSelectedId(it.id)}
                data-testid={`admin-inbox-row-${it.id}`}
                className={`w-full text-left px-4 sm:px-5 py-3.5 flex flex-col gap-1 hover:bg-slate-50 transition-colors ${
                  selectedId === it.id ? "bg-violet-50" : ""
                }`}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <StatusPill status={it.status} />
                  <span className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">
                    {(it.topic || "").replace(/-/g, " ")}
                  </span>
                  <span className="text-[11px] text-slate-400 ml-auto shrink-0">
                    {relTime(it.created_at)}
                  </span>
                </div>
                <div className="font-semibold text-slate-900 text-[14.5px] truncate">
                  {it.name}
                </div>
                <div className="text-[13px] text-slate-500 truncate">{it.email}</div>
                <div className="text-[13px] text-slate-600 line-clamp-1">{it.message}</div>
              </button>
            </li>
          ))}
        </ul>

        <section className="rounded-2xl ring-1 ring-slate-200 bg-white overflow-hidden" data-testid="admin-inbox-detail">
          {!selected ? (
            <div className="p-10 text-center text-sm text-slate-400">Pick a message from the list to triage it.</div>
          ) : (
            <div className="p-5 sm:p-6">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <StatusPill status={selected.status} />
                  <h2 className="mt-2 font-serif font-bold text-slate-900 text-xl sm:text-2xl leading-tight">
                    {selected.name}
                  </h2>
                  <a
                    href={`mailto:${selected.email}?subject=${encodeURIComponent(`Re: your SocialPulse message (${selected.topic})`)}`}
                    data-testid="admin-inbox-reply"
                    className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-violet-700 hover:text-violet-900"
                  >
                    <Mail className="w-3.5 h-3.5" /> {selected.email}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                <div className="text-right text-[11px] text-slate-500 shrink-0">
                  <p>Topic: <span className="font-semibold text-slate-700 uppercase tracking-wider">{selected.topic}</span></p>
                  <p>Site: <span className="font-semibold text-slate-700">{selected.site}</span></p>
                  <p className="mt-1">{fullTime(selected.created_at)}</p>
                </div>
              </div>

              <div className="mt-5 rounded-xl bg-slate-50 p-4 text-sm text-slate-800 whitespace-pre-wrap leading-relaxed border border-slate-100">
                {selected.message}
              </div>

              {selected.admin_note && (
                <div className="mt-3 rounded-xl bg-amber-50 ring-1 ring-amber-200 p-3 text-sm text-amber-900">
                  <p className="text-[10.5px] font-semibold uppercase tracking-wider text-amber-700 mb-1">Admin note</p>
                  {selected.admin_note}
                </div>
              )}

              <div className="mt-5 flex flex-wrap gap-2">
                {["new", "in_progress", "resolved", "spam"].map((s) => (
                  <button
                    key={s}
                    onClick={() => updateStatus(selected.id, s)}
                    disabled={selected.status === s}
                    data-testid={`admin-inbox-set-${s}`}
                    className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-xs font-semibold transition-colors ${
                      selected.status === s
                        ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                        : "bg-violet-600 hover:bg-violet-700 text-white"
                    }`}
                  >
                    Mark {s.replace("_", " ")}
                  </button>
                ))}
                <button
                  onClick={() => updateStatus(selected.id, NEXT_STATUS[selected.status] || "in_progress")}
                  data-testid="admin-inbox-next-step"
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white transition-colors"
                >
                  Move to next step →
                </button>
              </div>

              <p className="mt-6 text-[11px] text-slate-400">
                ID: {selected.id} · IP: {selected.ip || "unknown"}
              </p>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

const STATUS_STYLE = {
  new:         { pill: "bg-violet-50 text-violet-700 ring-violet-200",    label: "New" },
  in_progress: { pill: "bg-blue-50 text-blue-700 ring-blue-200",          label: "In progress" },
  resolved:    { pill: "bg-emerald-50 text-emerald-700 ring-emerald-200", label: "Resolved" },
  spam:        { pill: "bg-rose-50 text-rose-700 ring-rose-200",          label: "Spam" },
};

function StatusPill({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.new;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider ring-1 ${s.pill}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      {s.label}
    </span>
  );
}

function relTime(iso) {
  if (!iso) return "";
  try {
    const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60)   return `${s}s ago`;
    if (s < 3600) return `${Math.round(s / 60)}m ago`;
    if (s < 86400)return `${Math.round(s / 3600)}h ago`;
    if (s < 30 * 86400) return `${Math.round(s / 86400)}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch { return iso; }
}
function fullTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}
